
import Page from '@/Page.vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import moment from 'moment-timezone';
import { LocationModel, QueryOperator, ShiftModel, ShiftStateEnum, SortOrder, WorkplaceModel } from '@/libs/Api';

// Timezone pro zobrazování - vždy Praha bez ohledu na TZ prohlížeče
const DISPLAY_TIMEZONE = 'Europe/Prague';
import { workplacesStore } from '@/libs/workplaces/+state/store';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { locationsStore } from '@/libs/locations/+state/store';
import { shiftsStore } from '@/libs/shifts/+state/store';
import { CrudAction, CrudResponseAction } from '@/libs/core/+state/models/crud-action';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import EditShiftDetailDialog from '@/libs/shifts/components/EditShiftDetailDialog.vue';
import {
    getDayStartMinutes,
    startOfProductionDayMs,
    endOfProductionDayMs,
    startOfProductionDayIso,
    endOfProductionDayIso,
} from '@/libs/tpm-analytics/core/production-day.utils';

@Component({
    components: { EditShiftDetailDialog },
})
export default class ShiftManagementCalendar extends Page {
    @Prop() locationId!: string | null;
    @Prop() activeShiftId!: string | null;

    protected ShiftStateEnum: typeof ShiftStateEnum = ShiftStateEnum;

    protected date: Date = new Date();
    protected dateString = '';

    protected daysBefore = 3;
    protected daysAfter = 3;

    protected menu = false;
    protected editDialog = false;
    protected shiftId: string | null = null;
    protected editDialogInitialData: ShiftModel | null = null;
    protected editDialogInitialDate: string | null = null;

    protected headers = [{ text: this.$t('common.workplace'), value: 'name', sortable: false, align: 'left' }];

    get isLoadingShifts(): boolean {
        return shiftsStore.useGetter(CrudGetter.PendingBasic, 'LocationShiftCalendar');
    }

    @Watch('locationId')
    locationIdChanged(v: string | null, p: string | null) {
        if (v != p) {
            this.loadShifts();
        }
    }

    @Watch('dateString')
    dateStringChanged(v: string) {
        if (moment.tz(v, DISPLAY_TIMEZONE).isValid()) {
            this.setDate(moment.tz(v, DISPLAY_TIMEZONE).toDate());
        }
    }

    created() {
        if (this.getQueryInt('date', true)) {
            this.date = new Date(this.getQueryInt('date', true)!);
        } else {
            // Pokud je aktuální čas před začátkem výrobního dne, použijeme předchozí výrobní den
            const now = moment.tz(DISPLAY_TIMEZONE).valueOf();
            const dayStartMinutes = getDayStartMinutes();
            const currentTime = moment.tz(DISPLAY_TIMEZONE);
            const currentHour = currentTime.hour();
            const currentMinute = currentTime.minute();
            const currentMinutesFromMidnight = currentHour * 60 + currentMinute;

            if (currentMinutesFromMidnight < dayStartMinutes) {
                // Jsme před začátkem výrobního dne, použijeme včerejší výrobní den
                this.date = new Date(startOfProductionDayMs(now - 86400000)); // -1 den v ms
            } else {
                this.date = new Date(startOfProductionDayMs(now));
            }
        }
    }

    mounted() {
        if (this.locationId) {
            this.loadShifts();
        }
        this.$store.subscribe((mutation) => {
            if (mutation.type === shiftsStore.getActionName(CrudResponseAction.CreateSuccess)) {
                if (mutation.payload.item) {
                    const shift = mutation.payload.item as ShiftModel;
                    const workplaces = this.getWorkplaces();
                    // Kontrola, zda nově vytvořená směna patří do aktuálně zobrazovaného výrobního dne
                    const currentProdDayStart = startOfProductionDayMs(this.date.getTime());
                    const shiftProdDayStart = startOfProductionDayMs(new Date(shift.fromDate!).getTime());
                    if (
                        workplaces.some((x) => x.id == shift.workplaceId) &&
                        currentProdDayStart === shiftProdDayStart
                    ) {
                        this.loadShifts();
                    }
                }
            }
        });
    }

    get tableItems(): { workplace: WorkplaceModel; shifts: any[] }[] {
        const shifts = this.getShifts();
        const shiftNumbers = this.getShiftNumbers();
        const workplaces = this.getWorkplaces();
        return workplaces.map((x) => {
            let workplaceShifts = [] as (ShiftModel | null)[];
            for (const shiftNumber of shiftNumbers) {
                const filteredShifts = shifts.filter(
                    (s) => s.workplaceId == x.id && (s.shiftType?.shiftNumber ?? 0) == shiftNumber
                );
                filteredShifts.sort((a, b) => new Date(a.fromDate!).getDate() - new Date(b.fromDate!).getDate());
                if (filteredShifts.length > 0) {
                    workplaceShifts = [...workplaceShifts, ...filteredShifts];
                } else {
                    workplaceShifts.push(null);
                }
            }
            return {
                workplace: x,
                shifts: workplaceShifts,
            };
        });
    }

    getWorkplaces(): WorkplaceModel[] {
        const locationScopes = this.getLocationScopes();
        let workplaces: WorkplaceModel[] = workplacesStore.useGetter(CrudGetter.Data) ?? [];
        workplaces = workplaces
            .filter((x) => locationScopes.includes(x.locationId!))
            .sort((a, b) => (a.name ?? '').localeCompare((b.name ?? '').toString()));
        return workplaces;
    }

    getShiftNumbers(): number[] {
        let shifts = this.getShifts();
        let shiftNumbers = shifts
            .map((x) => x.shiftType?.shiftNumber ?? 0)
            .reduce((a: number[], b: number) => {
                if (!a.contains(b)) {
                    a.push(b);
                }
                return a;
            }, []);
        shiftNumbers = shiftNumbers
            .filter((x) => shifts.some((t) => (t.shiftType?.shiftNumber ?? 0) == x))
            .sort((a, b) => a - b);
        return shiftNumbers;
    }

    getShifts(): ShiftModel[] {
        return shiftsStore.useGetter(CrudGetter.DataBasic, 'LocationShiftCalendar') ?? [];
    }

    getLocationScopes() {
        const locations: LocationModel[] = locationsStore.useGetter(CrudGetter.Data) ?? [];
        return locations
            .filter((x) => x.locationParents!.some((y) => y.parentalLocationId! == this.locationId))
            .map((x) => x.id!)
            .concat([this.locationId ?? '']);
    }

    getDate(dayShift: number) {
        return moment.tz(DISPLAY_TIMEZONE).startOf('day').add(dayShift, 'days').toDate();
    }

    getStartOfDay(date: Date) {
        return moment.tz(date, DISPLAY_TIMEZONE).startOf('day').toDate();
    }

    isDate(e: Date, d: Date) {
        return e.toDateString() === d.toDateString();
    }

    setDate(e: Date) {
        this.date = e;
        // Vypočítáme začátek následujícího výrobního dne jako expirace
        const nextDayStart = new Date(endOfProductionDayMs(moment.tz(DISPLAY_TIMEZONE).valueOf()));
        this.setQuery('date', e.getTime().toString(), true, 'Q', nextDayStart);
        this.loadShifts();
    }

    loadShifts() {
        // Získáme začátek a konec výrobního dne jako ISO stringy s timezone offsetem
        // Formát: "2025-10-27T06:00:00+01:00" - zachovává správnou timezone
        const prodDayStartIso = startOfProductionDayIso(this.date);
        const prodDayEndIso = endOfProductionDayIso(this.date);

        shiftsStore.dispatch(
            CrudAction.GetAllBasic,
            createCrudQueryPayload(
                { field: 'fromDate', index: 1, order: SortOrder.Asc },
                [
                    {
                        field: 'fromDate',
                        op: QueryOperator.Lt,
                        comparand: prodDayEndIso,
                    },
                    {
                        field: 'toDate',
                        op: QueryOperator.Gt,
                        comparand: prodDayStartIso,
                    },
                    {
                        field: 'workplace.locationId',
                        op: QueryOperator.In,
                        comparand: this.getLocationScopes(),
                    },
                ],
                {
                    pageIndex: 1,
                    pageSize: 99999,
                },
                false,
                'LocationShiftCalendar'
            )
        );
    }

    onShiftClick(shift: ShiftModel) {
        this.$emit('shift-click', shift.id);
    }

    isHistoryDate(date: string) {
        return new Date(date).getTime() + 10 * 60 * 1000 < moment.tz(DISPLAY_TIMEZONE).valueOf();
    }

    onCreateShift(workplace: WorkplaceModel) {
        this.shiftId = null;
        this.editDialogInitialDate = moment.tz(this.date, DISPLAY_TIMEZONE).format('YYYY-MM-DD');
        this.editDialogInitialData = {
            shiftState: ShiftStateEnum.Waiting,
            workplaceId: workplace.id,
            workplace: workplace,
        };
        this.$nextTick(() => {
            this.editDialog = true;
        });
    }

    onShiftCreated(shift: ShiftModel) {
        this.onShiftClick(shift);
        this.shiftId = null;
        this.editDialog = false;
    }
}
